import { cellAtWorld, heroWalkable } from '../grid.ts';
import { imax, imin, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, Enemy, Hero, SendaNpc, StormRules, StormState } from '../types.ts';
import { damageEnemy, damageHero, ev, findEnemy, spawnEnemy } from './combat.ts';
import { orderMove } from './heroes.ts';
import { cubBurst, cubOnPerson, cubSpawn, cubStep, cubsAlive } from './cubs.ts';
import { passFlow, passLane } from './pass-layout.ts';
import { standBy } from './wedges.ts';

/**
 * The Ventisquero (docs/sendas/guerrero.md, chapter 5, act 4, and §5). A giant of packed snow and stone, three heroes
 * tall, no face, crossing the pass toward Ruy's line: if it crosses, it wins. It comes on in charges of two hexes; as
 * one ends, the two footprints of the next sink into the snow ahead, and the next comes no sooner than `foot` later (its
 * back loads with snow as the count runs). A wedge driven into one of the footprints trips it: the charge is cut, it
 * falls back two hexes and stands exposed a while (more of every blow). The wedges are in a pile by the line, a few at
 * a time; the hero carries two. Between charges it sweeps (its arm raised first). Its cubs go for the line: each one
 * that touches it pushes a shield back (the line gives: the Ventisquero has that much less to go); under Harl's banner
 * the middle gives less. Below `call` of its life it crouches and calls the slope down: the shadow by lanes, three rocks
 * with grain for the wedges (two right ones keep the snow off the line; the third throws it on), the snow taking the
 * shields it reaches; and the farms come up with Dalmo, horns and torches: Dalmo on the left wing stops one cub in two,
 * the farmers' torches on the right burn the cubs that reach them. At `breath`, it stops to breathe in the middle of the
 * pass: with Ruy's beam ready, the last stone set then brings the cornice down on it; without it, cubs come out of its
 * breath. At `harl`, Harl's shield, left in the middle of the pass sixty years ago, stands a hex in front of it: pulled
 * and set in the line's gap, the whole line closes, lights red, and the next charge breaks it. Wenn, if she climbed,
 * reads a name aloud now and then and that shield goes back to its place; if she stayed, the Ventisquero brings fewer
 * cubs. Lost with the hero down or the line crossed.
 *
 * Cost: one giant and a handful of cubs; the avalanche's flow is three rocks worked out once, when it falls.
 */

/** What the hero can be sent to (the `mark` command's `at`): a footprint, an avalanche's rock, the beam's stone, Harl's shield and its slot, a person to lift, the banner. */
export const STORM_ROCK = 10;
export const STORM_STONE = 20;
export const STORM_SHIELD = 21;
export const STORM_SLOT = 22;
export const STORM_LIFT = 30;
export const STORM_BANNER = 40;

/** A quarter of a hex, the line's give in (y units). */
export const QUARTER = 216;

const npc = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id);

/** How far it still has to go to cross the line (y units): its distance less what the line has given. */
export const stormLeft = (r: StormRules, w: StormState): number => {
  let give = 0;
  for (const b of w.back) give += b;
  return r.lineY - w.y - give * QUARTER;
};

/** Its life in bp of its whole (10000 full). */
const lifeBp = (e: Enemy | undefined): number => (e && e.maxHp > 0 ? Math.trunc((imax(0, e.hp) * 10000) / e.maxHp) : 0);

/** The next charge's footprints, a charge ahead of where it stands, a little to one side or the other. */
const marks = (s: BattleState, r: StormRules, w: StormState): void => {
  const nx = imax(-2600, imin(2600, w.x + rngRange(s.rng, -700, 700)));
  const ny = w.y + r.step;
  w.prints = [
    { x: nx - 380, y: ny },
    { x: nx + 380, y: ny },
  ];
  w.wedged = -1;
};

const fresh = (s: BattleState, r: StormRules): StormState => {
  const y0 = r.lineY - r.start;
  const e = spawnEnemy(s, 'giant', 1, false, 0, 0, y0, r.hp);
  e.damage = 0;
  e.facing = 256;
  const w: StormState = {
    boss: e.id,
    x: 0,
    y: y0,
    y0,
    next: s.tick + r.first,
    prints: [],
    wedged: -1,
    exposed: 0,
    sweep: -1,
    sx: 0,
    sy: 0,
    sweepNext: s.tick + r.sweep.every,
    charges: 0,
    trips: 0,
    hand: r.hand,
    pile: r.pile,
    restock: s.tick + r.restock,
    at: -1,
    held: 0,
    back: r.slots.map(() => 0),
    holes: r.slots.map((_, i) => i === 5),
    fall: -1,
    fell: false,
    shut: r.rocks.flat().map(() => 0),
    breath: -1,
    stone: false,
    corn: -1,
    shield: 0,
    shieldAt: { x: 0, y: 0 },
    closed: false,
    broken: false,
    banner: true,
    farms: false,
    dalmoTurn: 0,
    torches: new Array<number>(r.torches).fill(0),
    read: s.tick + r.read,
    cub: s.tick + r.cubs.first,
    cubs: [],
    left: 0,
  };
  marks(s, r, w);
  w.left = stormLeft(r, w);
  return w;
};

/** The avalanche's rocks in one list (row by row). */
export const stormRocks = (r: StormRules): { lane: number; dir: number; x: number; y: number }[] => r.rocks.flat();

/** Where the hero goes for each job. */
const spotOf = (s: BattleState, r: StormRules, w: StormState, at: number): { x: number; y: number } | null => {
  if (at === 0 || at === 1) return w.prints[at] ?? null;
  if (at >= STORM_ROCK && at < STORM_STONE) {
    const k = stormRocks(r)[at - STORM_ROCK];
    return k ? { x: k.x, y: k.y + 700 } : null;
  }
  if (at === STORM_STONE) return r.beamAt;
  if (at === STORM_SHIELD) return w.shield === 1 ? w.shieldAt : null;
  if (at === STORM_SLOT) return w.shield === 2 ? (r.slots[5] ?? null) : null;
  if (at === STORM_BANNER) return r.bannerAt;
  if (at >= STORM_LIFT && at < STORM_BANNER) {
    const n = s.senda?.npcs[at - STORM_LIFT];
    return n ? { x: n.x, y: n.y } : null;
  }
  return null;
};

/** Where a job is (null: nothing there now). */
export const stormSpot = (s: BattleState, at: number): { x: number; y: number } | null => {
  const r = s.rules?.senda?.storm;
  const w = s.senda?.storm;
  return r && w ? spotOf(s, r, w, at) : null;
};

/** The hero sent to one of the pass's jobs (the `mark` command). */
export const stormOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.storm;
  const w = s.senda?.storm;
  if (!r || !w) return;
  const p = spotOf(s, r, w, at);
  if (!p) return;
  w.at = at;
  w.held = 0;
  orderMove(s, h, p.x, p.y);
  h.target = 0;
  h.forced = false;
};

/** Nearer the line than this, a hero is pushed by a charge (the Ventisquero's breadth). */
const BREADTH = 1300;

/** A hero pushed toward the line (+y) as far as open ground goes. */
const shove = (s: BattleState, h: Hero, d: number): void => {
  let y = h.y;
  for (let k = 0; k < d; k += 200) {
    const c = cellAtWorld(s, h.x, y + 200);
    if (c < 0 || !heroWalkable(s, c)) break;
    y += 200;
  }
  h.y = h.destY = h.ay = y;
  h.destX = h.ax = h.x;
  h.path = [];
  h.moving = false;
};

const charge = (s: BattleState, r: StormRules, w: StormState, e: Enemy, cue: (c: string) => void): void => {
  const below = lifeBp(e) <= r.mid;
  if (w.closed) {
    // The whole line, lit: it breaks against it.
    w.broken = true;
    e.hp = 0;
    const pid = s.heroes[0]?.pid;
    if (pid) e.lastPid = pid;
    ev(s, { t: 'senda', kind: 'broken', npc: '', x: w.x, y: w.y });
    ev(s, { t: 'boom', x: w.x, y: w.y, r: 3400, kind: 'cornice' });
    cue('broken');
    return;
  }
  if (w.wedged >= 0) {
    // Tripped on the wedge: back, and open to blows.
    w.trips++;
    w.y = imax(w.y0, w.y - r.back);
    w.exposed = s.tick + r.exposed;
    ev(s, { t: 'senda', kind: 'trip', npc: '', x: w.x, y: w.y, n: w.trips });
    cue(`trip${w.trips}`);
  } else {
    const p = w.prints[0];
    const nx = p ? p.x + 380 : w.x;
    const y1 = w.y + r.step;
    for (const h of s.heroes) {
      if (h.downTicks > 0 || Math.abs(h.x - nx) > BREADTH || h.y < w.y - 500 || h.y > y1 + 700) continue;
      damageHero(s, h, mulBp(h.stats.maxHp, r.chargeBp), e, 'physical', true);
      shove(s, h, r.chargePush);
      h.stun = imax(h.stun, 12);
    }
    w.x = nx;
    w.y = y1;
    w.charges++;
    ev(s, { t: 'senda', kind: 'charge', npc: '', x: w.x, y: w.y, n: w.charges });
    cue(`charge${w.charges}`);
  }
  e.x = w.x;
  e.y = w.y;
  w.next = s.tick + (below ? r.every[1] : r.every[0]);
  // One sweep between charges, a while after this one.
  w.sweepNext = s.tick + r.sweep.every;
  marks(s, r, w);
};

/** The hero's hands at a job: standing still by it long enough. */
const handWork = (s: BattleState, r: StormRules, w: StormState, cue: (c: string) => void): void => {
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (!h || w.at < 0) return;
  const at = w.at;
  const p = spotOf(s, r, w, at);
  if (!p) {
    w.at = -1;
    return;
  }
  const reach = at >= STORM_ROCK && at < STORM_STONE ? 1150 : r.reach + (at >= STORM_LIFT ? 400 : 0);
  if (!standBy(h, w, p.x, p.y, reach)) return;
  const need = at >= STORM_LIFT && at < STORM_BANNER ? r.lift : at === STORM_BANNER ? r.lift : r.stand;
  if (++w.held < need) return;
  w.held = 0;
  w.at = -1;
  if (at === 0 || at === 1) {
    if (w.hand <= 0 || w.wedged >= 0) return;
    w.hand--;
    w.wedged = at;
    ev(s, { t: 'senda', kind: 'plant', npc: '', x: p.x, y: p.y, n: at });
    cue('plant');
  } else if (at >= STORM_ROCK && at < STORM_STONE) {
    const k = at - STORM_ROCK;
    if (w.shut[k] === 1) {
      w.shut[k] = 0;
      w.hand++;
      ev(s, { t: 'senda', kind: 'pull', npc: '', x: p.x, y: p.y, n: k });
    } else if (w.hand > 0) {
      w.shut[k] = 1;
      w.hand--;
      ev(s, { t: 'senda', kind: 'wedge', npc: '', x: p.x, y: p.y, n: k });
    }
  } else if (at === STORM_STONE) {
    if (!r.ready || w.stone || w.breath <= 0) return;
    w.stone = true;
    w.corn = s.tick + 36;
    ev(s, { t: 'senda', kind: 'stone', npc: '', x: p.x, y: p.y });
    cue('stone');
  } else if (at === STORM_SHIELD) {
    w.shield = 2;
    ev(s, { t: 'senda', kind: 'take', npc: '', x: p.x, y: p.y });
    cue('pulled');
  } else if (at === STORM_SLOT) {
    w.shield = 3;
    w.closed = true;
    w.holes = w.holes.map(() => false);
    w.back = w.back.map(() => 0);
    ev(s, { t: 'senda', kind: 'lineRed', npc: '', x: p.x, y: p.y });
    cue('closed');
  } else if (at === STORM_BANNER) {
    w.banner = true;
    ev(s, { t: 'senda', kind: 'banner', npc: '', x: p.x, y: p.y, n: 1 });
  } else {
    const n = s.senda?.npcs[at - STORM_LIFT];
    if (n && n.hp <= 0) {
      n.hp = n.maxHp;
      ev(s, { t: 'senda', kind: 'lift', npc: n.id, x: n.x, y: n.y });
    }
  }
};

/** A cub reaching what it went for. */
const cubLands = (s: BattleState, r: StormRules, w: StormState, e: Enemy, to: number, cue: (c: string) => void): void => {
  if (to === -3) {
    if (w.banner) {
      w.banner = false;
      ev(s, { t: 'senda', kind: 'banner', npc: '', x: r.bannerAt.x, y: r.bannerAt.y, n: 0 });
      cue('bannerDown');
    }
    cubBurst(s, e);
    return;
  }
  if (to <= -10) {
    const n = s.senda?.npcs[-10 - to];
    if (n && n.hp > 0) cubOnPerson(s, e, n, r.cub.blows);
    else cubBurst(s, e);
    return;
  }
  const p = r.slots[to];
  if (!p) return cubBurst(s, e);
  // The farms' wings, once they're up: Dalmo on the left stops one in two; on the right, a lit torch burns it.
  if (w.farms && p.x < -1200) {
    const dalmo = npc(s, 'dalmo');
    if (dalmo && dalmo.hp > 0 && w.dalmoTurn++ % 2 === 0) {
      dalmo.hp--;
      ev(s, { t: 'senda', kind: dalmo.hp > 0 ? 'hit' : 'down', npc: 'dalmo', x: dalmo.x, y: dalmo.y });
      return cubBurst(s, e);
    }
  }
  if (w.farms && p.x > 1200) {
    const j = torchOf(r, to);
    if (j >= 0 && (w.torches[j] as number) <= s.tick) {
      w.torches[j] = s.tick + r.torchOut;
      ev(s, { t: 'senda', kind: 'torch', npc: '', x: p.x, y: p.y + 500, n: j });
      return cubBurst(s, e);
    }
  }
  const mid = to >= 4 && to <= 6;
  const n = w.holes[to] && !(mid && w.banner) ? r.gapShove : mid && w.banner ? r.banner : r.shove;
  w.back[to] = (w.back[to] as number) + n;
  ev(s, { t: 'senda', kind: 'shove', npc: '', x: p.x, y: p.y, n: to });
  cubBurst(s, e);
};

/** The torch (0 to 3) that stands by a slot of the right wing (−1: none). */
export const torchOf = (r: StormRules, slot: number): number => {
  const right = r.slots.map((p, i) => ({ x: p.x, i })).filter((p) => p.x > 1200).sort((a, b) => a.x - b.x);
  const k = right.findIndex((p) => p.i === slot);
  return k < 0 ? -1 : imin(r.torches - 1, Math.trunc((k * r.torches) / imax(1, right.length)));
};

/** The giant and its cubs are the act's own: nothing else moves them (true). */
export const stormHold = (s: BattleState, e: Enemy): boolean => {
  const w = s.senda?.storm;
  if (!w || !s.rules?.senda?.storm) return false;
  if (e.id === w.boss) {
    if (e.stun > 0) {
      e.stun--;
      // Stunned, its count runs a third slower (as every boss's).
      if (s.tick % 3 === 0) w.next++;
      e.anim = 'stun';
      return true;
    }
    e.x = w.x;
    e.y = w.y;
    e.facing = 256;
    e.anim = w.breath > 0 || (w.fall >= 0 && !w.fell) ? 'idle' : w.sweep >= 0 ? 'attack' : 'idle';
    return true;
  }
  return w.cubs.some((c) => c.id === e.id);
};

/** Its exposed blows (tripped on a wedge): more of each. */
export const stormTakes = (s: BattleState, e: Enemy, dmg: number): number => {
  const r = s.rules?.senda?.storm;
  const w = s.senda?.storm;
  return r && w && e.id === w.boss && s.tick < w.exposed ? mulBp(dmg, r.exposedBp) : dmg;
};

export const stormTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.storm;
  const st = s.senda;
  if (!r || !st || !s.started) return;
  const w = (st.storm ??= fresh(s, r));
  const e = findEnemy(s, w.boss);
  if (!e || e.hp <= 0 || w.broken) return;
  const life = lifeBp(e);
  const h = s.heroes.find((x) => x.downTicks === 0);
  // The pile by the line: one more every so often; a hero by it with room in its hands takes one.
  if (s.tick >= w.restock) {
    w.restock = s.tick + r.restock;
    w.pile = imin(r.pile, w.pile + 1);
  }
  if (h && w.hand < r.hand && w.pile > 0 && s.tick % 6 === 0 && withinRange(h.x, h.y, r.pileAt.x, r.pileAt.y, 1100)) {
    w.hand++;
    w.pile--;
    ev(s, { t: 'senda', kind: 'take', npc: '', x: r.pileAt.x, y: r.pileAt.y });
  }
  handWork(s, r, w, cue);
  // The avalanche, called at `call`: its shadow `warn` before; it crouches the last `crouch`; the farms come up.
  if (w.fall < 0 && life <= r.call.at) {
    w.fall = s.tick + r.call.warn;
    w.farms = true;
    for (const n of st.npcs) n.away = false;
    ev(s, { t: 'senda', kind: 'call', npc: '', x: w.x, y: w.y });
    cue('call');
    w.next = imax(w.next, w.fall + r.foot);
  }
  if (w.fall >= 0 && !w.fell && s.tick >= w.fall) {
    w.fell = true;
    const g = passFlow(r.rocks, r.snow, w.shut);
    let lost = 0;
    r.slots.forEach((p, i) => {
      const lane = passLane(p.x);
      if (lane >= 0 && (g[lane] as number) > 0 && !w.holes[i]) {
        w.holes[i] = true;
        lost++;
      }
    });
    for (const x of s.heroes) {
      const lane = passLane(x.x);
      if (x.downTicks > 0 || lane < 0 || !(g[lane] as number) || x.y < r.lineY - 4000) continue;
      damageHero(s, x, mulBp(x.stats.maxHp, r.call.heroBp), null, 'physical', true);
      x.stun = imax(x.stun, 18);
    }
    for (const c of w.cubs) {
      const ce = findEnemy(s, c.id);
      if (ce && ce.hp > 0 && (g[passLane(ce.x)] ?? 0) > 0) cubBurst(s, ce);
    }
    ev(s, { t: 'senda', kind: 'avalanche', npc: '', x: 0, y: r.lineY, n: lost });
    cue(lost ? 'buried' : 'split');
  }
  // The breath at `breath`: it stops in the middle of the pass.
  if (w.breath < 0 && life <= r.breath.at) {
    w.breath = s.tick + r.breath.ticks;
    w.sweep = -1;
    w.next = imax(w.next, w.breath + r.foot);
    ev(s, { t: 'senda', kind: 'breath', npc: '', x: w.x, y: w.y });
    cue(r.ready ? 'breathReady' : 'breath');
  }
  if (w.breath > 0 && s.tick >= w.breath) {
    w.breath = 0;
    if (!w.stone) for (let i = 0; i < r.breath.cubs; i++) cubSpawn(s, w.cubs, { x: w.x + (i - 1) * 600, y: w.y + 900 }, rngInt(s.rng, r.slots.length), r.cub.hp);
  }
  if (w.corn >= 0 && s.tick >= w.corn) {
    w.corn = -1;
    const pid = s.heroes[0]?.pid ?? '';
    damageEnemy(s, e, mulBp(e.maxHp, r.breath.cornBp), { pid, type: 'physical' });
    for (const c of w.cubs) {
      const ce = findEnemy(s, c.id);
      if (ce && ce.hp > 0) cubBurst(s, ce);
    }
    ev(s, { t: 'senda', kind: 'cornice', npc: '', x: w.x, y: w.y });
    ev(s, { t: 'boom', x: w.x, y: w.y, r: 3000, kind: 'cornice' });
    cue('cornice');
    if (e.hp <= 0) return;
  }
  // Harl's shield at `harl`: a hex in front of it.
  if (w.shield === 0 && lifeBp(e) <= r.harl.at) {
    w.shield = 1;
    w.shieldAt = { x: w.x, y: w.y + 1000 };
    ev(s, { t: 'senda', kind: 'harlShield', npc: '', x: w.shieldAt.x, y: w.shieldAt.y });
    cue('harl');
  }
  // The charge (not while it crouches or breathes).
  const still = w.breath > 0 || (w.fall >= 0 && !w.fell && s.tick >= w.fall - r.call.crouch);
  if (!still && s.tick >= w.next) charge(s, r, w, e, cue);
  if (w.broken) return;
  // A sweep between charges: its arm up `warn` before, at the hero.
  if (w.sweep < 0 && !still && h && s.tick >= w.sweepNext && w.next - s.tick > r.sweep.warn + 6 && withinRange(h.x, h.y, w.x, w.y, 3600)) {
    w.sweep = s.tick + r.sweep.warn;
    w.sx = h.x;
    w.sy = h.y;
    ev(s, { t: 'senda', kind: 'sweepWarn', npc: '', x: w.sx, y: w.sy, n: r.sweep.warn });
  }
  if (w.sweep >= 0 && s.tick >= w.sweep) {
    w.sweep = -1;
    w.sweepNext = w.next + 1;
    for (const x of s.heroes) if (x.downTicks === 0 && withinRange(x.x, x.y, w.sx, w.sy, r.sweep.r)) damageHero(s, x, mulBp(x.stats.maxHp, r.sweep.bp), e, 'physical', true);
    ev(s, { t: 'senda', kind: 'sweep', npc: '', x: w.sx, y: w.sy });
  }
  // Its cubs: a band every so often, for the line (the banner, Wenn now and then).
  if (s.tick >= w.cub) {
    w.cub = s.tick + r.cubs.every;
    let n = (life <= r.mid ? r.cubs.n[1] : r.cubs.n[0]) - r.scars;
    if (!r.climbed) n = Math.trunc((n * 8000 + 5000) / 10000);
    const wenn = st.npcs.findIndex((x) => x.id === 'wenn');
    for (let i = 0; i < imax(1, n); i++) {
      const k = rngInt(s.rng, 10);
      const to = k === 0 && w.banner ? -3 : k === 1 && r.climbed && wenn >= 0 && (st.npcs[wenn] as SendaNpc).hp > 0 ? -10 - wenn : rngInt(s.rng, r.slots.length);
      cubSpawn(s, w.cubs, { x: w.x + rngRange(s.rng, -900, 900), y: w.y + 700 }, to, r.cub.hp);
    }
  }
  cubsAlive(s, w.cubs);
  for (const c of w.cubs) {
    const ce = findEnemy(s, c.id);
    if (!ce) continue;
    const p = c.to === -3 ? r.bannerAt : c.to <= -10 ? st.npcs[-10 - c.to] : r.slots[c.to];
    if (!p) {
      cubBurst(s, ce);
      continue;
    }
    if (withinRange(ce.x, ce.y, p.x, p.y, 350)) {
      cubLands(s, r, w, ce, c.to, cue);
      continue;
    }
    cubStep(s, ce, p.x, p.y, r.cub, 26);
  }
  // Wenn reads a name: that shield back in its place.
  if (r.climbed && s.tick >= w.read) {
    w.read = s.tick + r.read;
    const wenn = npc(s, 'wenn');
    if (wenn && wenn.hp > 0) {
      let k = -1;
      w.back.forEach((b, i) => {
        if (b > 0 && (k < 0 || b > (w.back[k] as number))) k = i;
      });
      if (k >= 0) {
        w.back[k] = 0;
        ev(s, { t: 'senda', kind: 'read', npc: 'wenn', x: wenn.x, y: wenn.y, n: k });
        cue('read');
      }
    }
  }
  w.left = stormLeft(r, w);
};

/** Won with the Ventisquero down or broken on the line; lost with the line crossed. */
export const stormResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.senda?.storm;
  const w = s.senda?.storm;
  if (!r || !w) return null;
  const e = findEnemy(s, w.boss);
  if (w.broken || !e || e.hp <= 0) return 'won';
  return stormLeft(r, w) <= 0 ? 'lost' : null;
};

/** Its life taken (its half leaves a scar on a lost try). */
export const stormGoal = (s: BattleState): { done: number; need: number } => {
  const w = s.senda?.storm;
  const e = w ? findEnemy(s, w.boss) : undefined;
  if (!w || !e) return { done: w?.broken ? 1 : 0, need: 1 };
  return { done: e.maxHp - imax(0, e.hp), need: e.maxHp };
};
